import { AddOn } from 'store/resource/types';

/**
 * One owning party's section on the post-booking add-on step. `key` is the lower-cased owning-party
 * uuid and is what the grey-out compares on; `name` is optional, because a company can legitimately
 * have none and such a section renders without a heading.
 */
export interface PartySection {
  key: string;
  name?: string;
  /* VIS-2193. The party's logo from helix (a full url), when it has one. */
  logoUrl?: string;
  isLandlord: boolean;
  addOns: Array<AddOn>;
}

/**
 * Add-ons with no owning party share one bucket. Defensive -- native always sets the field -- but a
 * sentinel keeps them from collapsing under a key that reads as a real party.
 */
export const UNATTRIBUTED_PARTY_KEY = '';

/**
 * Group a flat add-on payload into one section per owning party, ordered for display.
 *
 * Three things that look like details and are not:
 *
 * 1. The key is lower-cased HERE. `owning_party` on this *catalog* shape is not normalised on the
 *    client (only the selection-slice copy is), and `catalog_company_uuid` collates case-insensitively
 *    in MySQL while JS string identity does not -- so keying the raw value renders one party as two
 *    sections and breaks the grey-out, since neither key disables the other.
 *
 * 2. `sort_order` is not a rank: gaps and duplicates are legal, and all-zero is common because an
 *    omitted field stores 0. Sorting by it is stable, so ties keep payload order. Never index by it.
 *
 * 3. Sections come from the add-ons present, never a fixed party list -- otherwise a landlord with no
 *    attached products renders a header with no rows under it.
 *
 * Section order is the landlord first, then every other party in first-appearance order.
 */
/* The section an add-on belongs to, created on first sight of its party. */
const bucket = (sections: Map<string, PartySection>, addOn: AddOn): void => {
  const key = addOn.owning_party ? addOn.owning_party.toLowerCase() : UNATTRIBUTED_PARTY_KEY;
  const existing = sections.get(key);

  if (existing) {
    existing.addOns.push(addOn);

    return;
  }

  sections.set(key, {
    key,
    // An empty string is treated as absent, matching what the read already does server-side.
    name: addOn.owning_party_name || undefined,
    logoUrl: addOn.owning_party_logo_url || undefined,
    isLandlord: addOn.owning_party_is_landlord === true,
    addOns: [addOn],
  });
};

export const groupAddOnsByParty = (addOns: Array<AddOn> = []): Array<PartySection> => {
  const sections = new Map<string, PartySection>();

  addOns.filter(Boolean).forEach(addOn => bucket(sections, addOn));

  const ordered = [...sections.values()];

  // Stable, so equal `sort_order` keeps payload order. Missing reads as 0, which is what an omitted
  // field is stored as anyway.
  ordered.forEach(section => section.addOns.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)));

  // Also stable: only the landlord moves, every other party keeps first-appearance order.
  return ordered.sort((a, b) => Number(b.isLandlord) - Number(a.isLandlord));
};
